import {
  ERROR_LOGIN_REQUEST,
  ERROR_INVALID_LOGIN_CREDENTIALS,
  ERROR_SIGN_UP_EMAIL_IN_USE,
  ERROR_SIGN_UP_REQUEST,
  ERROR_SIGN_UP_API_EMAIL_IN_USE,
  ERROR_SENDING_RESET_PASSWORD_EMAIL,
  ERROR_RESETTING_PASSWORD,
} from '@/utils/messages';
import { errorlog } from '@/libs/error-logs';
import { toast_error, toast_warning } from '@/libs/toast-wrappers';
import { router } from '@/router/root';
import { ROUTES } from '@/router/routes';
import { toError } from '@/utils/errors';

import { ENDPOINTS } from './endpoints';
import { POST } from './base/index';
import { StandardResponse } from './standard-response';

type LoginResponseAPI = {
  'Access Key': string;
  'Access Denied'?: string;
  '2fa_required'?: boolean;
  pre_auth_token?: string;
};

export const login = async (email: string, password: string) => {
  try {
    const response = await POST<LoginResponseAPI>(ENDPOINTS.LOGIN, {
      email,
      password,
    });
    if ('Access Denied' in response && response['Access Denied']) {
      if (response['Access Denied'].includes('change your password')) {
        void router.navigate(ROUTES.CHANGE_PASSWORD);
        toast_warning(response['Access Denied']);
        return;
      }
      // default error message
      toast_error(ERROR_INVALID_LOGIN_CREDENTIALS);
      throw new Error(response['Access Denied']);
    }

    return response;
  } catch (e) {
    const error = e as Error;

    // dont show error message for invalid password
    if (!error.message.includes('Password is invalid')) {
      toast_error(ERROR_LOGIN_REQUEST);
    }

    errorlog(error, 'src/api/auth/login', 'Error logging in');
    return Promise.reject(error);
  }
};

export type SignUpRequest = {
  name: string;
  user_email: string;
  phone: string;
  city: string;
  provinceState: string;
  country: string;
  requestedRole: string;
  address?: string;
  postal?: string;
  clinic?: string;
  created_at?: string;
};

export type SignUpResponse = StandardResponse<{ result: string; signup_id: string }>;
export const signUp = async (form: SignUpRequest) => {
  try {
    const response = await POST<SignUpResponse>(ENDPOINTS.SIGNUP, form);
    if (response.data.result === ERROR_SIGN_UP_API_EMAIL_IN_USE) {
      toast_error(ERROR_SIGN_UP_EMAIL_IN_USE);
      return;
    }
    return response.data.signup_id;
  } catch (error) {
    errorlog(error as Error, 'src/api/auth/signup', 'Error signing up');
    toast_error(ERROR_SIGN_UP_REQUEST);
    return Promise.reject(toError(error));
  }
};

export const forgotPassword = async (email: string, passResetURL: string) => {
  try {
    const response = await POST(ENDPOINTS.FORGOT, {
      email,
      passResetURL,
    });
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/auth/forgotPassword', 'Error sending password reset email');
    toast_error(ERROR_SENDING_RESET_PASSWORD_EMAIL);
    return Promise.reject(toError(error));
  }
};

export const resetPassword = async (
  email: string,
  newPassword: string,
  confirmNewPassword: string,
  token: string
) => {
  try {
    const response = await POST(
      ENDPOINTS.RESET,
      {
        email,
        new_password: newPassword,
        confirm_new_password: confirmNewPassword,
      },
      { query: { token } }
    );
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/auth/resetPassword', 'Error resetting password');
    toast_error(ERROR_RESETTING_PASSWORD);
    return Promise.reject(toError(error));
  }
};

export const changePassword = async (
  email: string,
  newPassword: string,
  confirmNewPassword: string,
  currentPassword: string
) => {
  try {
    const response = await POST(ENDPOINTS.CHANGEPASS, {
      email,
      newPassword,
      confirmNewPassword,
      currentPassword,
    });
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/auth/changePassword', 'Error changing password');
    return Promise.reject(toError(error));
  }
};
